import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import {
  getLocalizedEntityField,
  getLocalizedText,
  getNormalizedLanguageCode,
} from '../../../miniapp/i18n/localization';
import {
  getAvailableWeights,
  getDefaultWeight,
  getWeightLabel,
  getWeightPrice,
} from '../../../miniapp/utils/weight';
import Icon from '../../../miniapp/ui-kit/Icon';
import IconButton from '../../../miniapp/ui-kit/IconButton';
import { LoadingState } from '../../../miniapp/ui-kit/Spinner';

export default function ProductScreen() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);
  const { catalogLoading, products, addToCart, cartCount, appConfig } = useWebsiteAppContext();

  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedWeight, setSelectedWeight] = useState(1);

  const parsedProductId = Number.parseInt(productId ?? '', 10);
  const isValidId = Number.isInteger(parsedProductId);
  const selectedProduct = isValidId
    ? (products.find((item) => item.id === parsedProductId) ?? null)
    : null;

  const productName = selectedProduct
    ? getLocalizedEntityField(selectedProduct, 'name', languageCode)
    : '';
  const productDescription = selectedProduct
    ? getLocalizedEntityField(selectedProduct, 'description', languageCode) ||
      t('product.noDescription')
    : '';
  const categoryName = selectedProduct?.category
    ? getLocalizedEntityField(selectedProduct.category, 'name', languageCode)
    : '';
  const manufacturerName = selectedProduct?.manufacturer
    ? getLocalizedEntityField(selectedProduct.manufacturer, 'name', languageCode)
    : '';

  // Reset per-product selection when navigating to another product (derived during render,
  // see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  const [selectionProductId, setSelectionProductId] = useState<number | null>(null);
  if (selectedProduct && selectedProduct.id !== selectionProductId) {
    setSelectionProductId(selectedProduct.id);
    setSelectedWeight(getDefaultWeight(selectedProduct));
    setSelectedImageIndex(0);
  }

  if (catalogLoading) {
    return (
      <LoadingState label={t('common.loading')} />
    );
  }

  if (!isValidId || !selectedProduct) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">{t('website.product.notFound')}</h2>
        <button
          onClick={() => navigate('/')}
          className="bg-accent text-on-accent px-6 py-2 rounded-full border-none cursor-pointer"
        >
          {t('success.backToCatalog')}
        </button>
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(selectedProduct.id, selectedWeight);
    }
  };

  const freeDeliveryThreshold = appConfig?.freeDeliveryThreshold ?? 150;
  const effectiveWeights = getAvailableWeights(selectedProduct);
  const displayPrice = getWeightPrice(selectedProduct, selectedWeight);
  const showPreviousImage = () => {
    setSelectedImageIndex((current) =>
      current === 0 ? selectedProduct.images.length - 1 : current - 1,
    );
  };
  const showNextImage = () => {
    setSelectedImageIndex((current) =>
      current === selectedProduct.images.length - 1 ? 0 : current + 1,
    );
  };

  return (
    <div className="flex flex-col gap-8 max-w-[1200px] mx-auto pb-20">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Link to="/" className="hover:text-accent no-underline transition-colors">
          {t('header.titleCatalog')}
        </Link>
        <Icon name="chevron_right" className="text-xs" />
        {categoryName && (
          <>
            <Link
              to={`/category/${selectedProduct.categoryId}`}
              className="hover:text-accent no-underline transition-colors"
            >
              {categoryName}
            </Link>
            <Icon name="chevron_right" className="text-xs" />
          </>
        )}
        <span className="text-text font-medium truncate">{productName}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left: Image Gallery */}
        <div className="flex flex-col gap-4">
          <div
            className="relative aspect-square rounded-[32px] overflow-hidden bg-secondary border border-border shadow-sm"
            role={selectedProduct.images.length > 1 ? 'region' : undefined}
            aria-roledescription={
              selectedProduct.images.length > 1 ? t('productCard.carousel') : undefined
            }
            aria-label={
              selectedProduct.images.length > 1
                ? t('productCard.carouselLabel', { name: productName })
                : undefined
            }
          >
            {selectedProduct.images.length > 0 ? (
              <>
                <img
                  src={selectedProduct.images[selectedImageIndex]?.url}
                  alt={
                    selectedProduct.images.length > 1
                      ? t('productCard.imageAlt', {
                          name: productName,
                          current: selectedImageIndex + 1,
                          total: selectedProduct.images.length,
                        })
                      : productName
                  }
                  className="w-full h-full object-cover transition-transform hover:scale-[1.02] duration-200 motion-reduce:transition-none motion-reduce:hover:scale-100"
                />
                {selectedProduct.images.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] border border-border bg-carousel-control text-text shadow-ambient transition-colors hover:bg-carousel-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:bg-surface-high motion-reduce:transition-none"
                      onClick={showPreviousImage}
                      aria-label={t('productCard.previousImage')}
                    >
                      <Icon name="chevron_left" className="text-[26px] !leading-none" />
                    </button>
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] border border-border bg-carousel-control text-text shadow-ambient transition-colors hover:bg-carousel-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:bg-surface-high motion-reduce:transition-none"
                      onClick={showNextImage}
                      aria-label={t('productCard.nextImage')}
                    >
                      <Icon name="chevron_right" className="text-[26px] !leading-none" />
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <div className="flex items-center justify-center h-full w-full bg-surface-high text-text-secondary">
                <Icon name="image" className="text-4xl" />
              </div>
            )}
          </div>

          {selectedProduct.images.length > 1 && (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {selectedProduct.images.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all p-0 cursor-pointer flex-shrink-0 ${
                    idx === selectedImageIndex
                      ? 'border-accent shadow-md'
                      : 'border-transparent hover:border-border'
                  }`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                  <span className="sr-only">
                    {t('productCard.showImage', {
                      current: idx + 1,
                      total: selectedProduct.images.length,
                    })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Product Details */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              {categoryName && (
                <span className="text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full text-accent bg-accent/10">
                  {categoryName}
                </span>
              )}
              {manufacturerName && (
                <Link
                  to={`/manufacturer/${selectedProduct.manufacturerId}`}
                  className="text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full text-text-secondary bg-secondary no-underline hover:bg-secondary/80 transition-colors"
                >
                  {manufacturerName}
                </Link>
              )}
            </div>

            <h1 className="text-5xl font-bold -tracking-tight m-0 text-text leading-[1.1]">
              {productName}
            </h1>

            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-4xl font-bold text-accent">{formatCurrency(displayPrice)}</span>
              <span className="text-text-secondary">
                {selectedProduct.pricingUnit === 'PER_UNIT'
                  ? t(`product.pricingUnit.${selectedProduct.pricingUnit}`)
                  : `/ ${getWeightLabel(selectedProduct, selectedWeight, languageCode)}`}
              </span>
            </div>
          </div>

          {effectiveWeights.length > 1 ? (
            <div className="flex flex-wrap gap-3">
              {effectiveWeights.map((weight) => (
                <button
                  key={weight}
                  type="button"
                  onClick={() => setSelectedWeight(weight)}
                  className={`border-0 rounded-full px-4 py-2 cursor-pointer font-semibold transition-all ${
                    selectedWeight === weight
                      ? 'bg-accent-gradient text-on-accent shadow-lg shadow-accent/20'
                      : 'bg-secondary text-text-secondary hover:bg-surface-high'
                  }`}
                >
                  {getWeightLabel(selectedProduct, weight, languageCode)}
                </button>
              ))}
            </div>
          ) : null}

          <div className="flex flex-col gap-4">
            <p className="text-text-secondary leading-relaxed text-lg italic m-0">
              {productDescription}
            </p>
          </div>

          {selectedProduct.additionalProperties.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {selectedProduct.additionalProperties.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-secondary rounded-2xl p-4 flex items-center gap-4 border border-border/50"
                >
                  <div className="w-12 h-12 shrink-0 rounded-full bg-surface-high flex items-center justify-center">
                    <Icon name={entry.property.icon} className="text-accent" />
                  </div>
                  <div>
                    <p className="m-0 text-sm font-bold text-text">
                      {getLocalizedEntityField(entry.property, 'name', languageCode)}
                    </p>
                    <p className="m-0 text-xs text-text-secondary">
                      {getLocalizedText(entry.value, entry.translations, languageCode)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex flex-col gap-6 pt-4">
            <div className="flex gap-4 items-center">
              <div className="flex items-center bg-secondary rounded-2xl p-1 border border-border">
                <IconButton
                  variant="plain"
                  size="lg"
                  className="text-text hover:text-accent"
                  icon="remove"
                  iconClassName="text-2xl"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  label={t('website.cart.decreaseProductAria', { name: productName })}
                />
                <span className="w-12 text-center font-bold text-lg">{quantity}</span>
                <IconButton
                  variant="plain"
                  size="lg"
                  className="text-text hover:text-accent"
                  icon="add"
                  iconClassName="text-2xl"
                  onClick={() => setQuantity(quantity + 1)}
                  label={t('website.cart.increaseProductAria', { name: productName })}
                />
              </div>

              <button
                onClick={handleAddToCart}
                className="flex-1 h-[60px] bg-accent-gradient text-on-accent rounded-2xl border-none font-bold text-lg flex items-center justify-center gap-3 shadow-lg shadow-accent/20 cursor-pointer transition-transform active:scale-95 hover:opacity-95 p-0"
              >
                <Icon name="shopping_basket" />
                {t('product.addToCart')}
              </button>
            </div>

            <div className="flex items-center gap-3 text-sm text-text-secondary justify-center bg-secondary/50 py-3 rounded-xl border border-border/50">
              <Icon name="verified" className="text-success" />
              <span>{t('product.freeDeliveryInfo', { threshold: freeDeliveryThreshold })}</span>
            </div>
          </div>
        </div>
      </div>

      {cartCount > 0 && (
        <div className="fixed bottom-8 right-8 z-50">
          <Link
            to="/cart"
            className="flex items-center gap-3 bg-white text-accent px-6 py-4 rounded-2xl shadow-2xl border border-border no-underline font-bold hover:-translate-y-1 transition-transform"
          >
            <Icon name="shopping_bag" />
            {t('product.viewCart', { count: cartCount })}
          </Link>
        </div>
      )}
    </div>
  );
}
